import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

const TIPO_LABEL = {
  ticket_new: "Nuovo ticket",
  ticket_assigned: "Ticket assegnato",
  ticket_message: "Nuovo messaggio ticket",
  order_new: "Ordine",
  order_delivery: "Consegna",
  cantiere_update: "Cantiere aggiornato",
};

export default function NotificationBell({ groupId }) {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const loadCount = async () => {
    try { const { data } = await api.get("/notifications/count"); setCount(data.unread || 0); }
    catch (e) { console.warn("notifications count failed:", e?.message || e); }
  };
  const loadItems = async () => {
    try { const { data } = await api.get("/notifications?limit=30"); setItems(data); }
    catch (e) { console.warn("notifications list failed:", e?.message || e); }
  };
  useEffect(() => { loadCount(); const t = setInterval(loadCount, 30000); return () => clearInterval(t); }, []);
  useEffect(() => { if (open) loadItems(); }, [open]);

  const markRead = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); }
      catch (e) { console.warn("mark-read failed:", e?.message || e); }
    }
    setOpen(false);
    if (n.link_type === "ticket" && n.cantiere_id && groupId) {
      navigate(`/g/${groupId}/cantieri/${n.cantiere_id}?tab=ticket-tecnici`);
    } else if (n.cantiere_id && groupId) {
      navigate(`/g/${groupId}/cantieri/${n.cantiere_id}`);
    }
    loadCount(); loadItems();
  };
  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); }
    catch (e) { console.warn("mark-all-read failed:", e?.message || e); }
    loadCount(); loadItems();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notif-bell" className="relative">
          <Bell className="h-5 w-5" />
          {count > 0 && <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 bg-rose-600 text-white text-[10px] font-bold" data-testid="notif-count">{count > 99 ? "99+" : count}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800">
          <div className="font-semibold text-sm">Notifiche</div>
          {count > 0 && <Button size="sm" variant="ghost" onClick={markAllRead} data-testid="notif-mark-all"><CheckCheck className="h-3.5 w-3.5 mr-1" /> Segna tutte lette</Button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">Nessuna notifica</div>
          ) : items.map(n => (
            <button key={n.id} onClick={() => markRead(n)} data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 hover:bg-amber-50 dark:hover:bg-amber-950/20 ${!n.is_read ? "bg-blue-50/60 dark:bg-blue-950/20" : ""}`}>
              <div className="flex items-start gap-2">
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.is_read ? "bg-blue-500" : "bg-transparent"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold">{TIPO_LABEL[n.tipo] || n.tipo}</div>
                  <div className="text-sm font-medium truncate">{n.titolo}</div>
                  {n.testo && <div className="text-xs text-slate-500 line-clamp-2">{n.testo}</div>}
                  <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString("it-IT")}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
